'use client';

import React, { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useJigyasa, filterCallsDue, filterAppointmentsUpcoming, filterNewToday } from "./state";
import { SectionTitle, LineSwitcher } from "./parts";
import { EnquiryRow } from "./rows";
import { EnquiryDetailSheet, AddEnquirySheet } from "./sheets";
import { SmartSuggestionsSection } from "./intelligence";

export const OperatorPhone = () => {
  const { enquiries, lineEnquiries } = useJigyasa();
  const [selectedId, setSelectedId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const callsDue = useMemo(() => filterCallsDue(lineEnquiries), [lineEnquiries]);
  const appts = useMemo(() => filterAppointmentsUpcoming(lineEnquiries), [lineEnquiries]);
  const newToday = useMemo(() => filterNewToday(lineEnquiries), [lineEnquiries]);
  const selected = enquiries.find((e) => e.id === selectedId) || null;
  const dueIds = useMemo(
    () => new Set([...callsDue, ...appts].map((e) => e.id)),
    [callsDue, appts]
  );

  return (
    <div className="mx-auto max-w-md px-4 pb-32 pt-4">
      <LineSwitcher />
      {/* Smart Suggestions — proactive tasks */}
      <SmartSuggestionsSection onOpenEnquiry={(id) => setSelectedId(id)} />

      {/* Demos & visits — overdue (red, on top) + today + next 7 days.
          Renders ONLY when it has appointments. */}
      {appts.length > 0 && (
        <section className="mb-6" data-testid="mobile-appts-section">
          <SectionTitle
            testId="section-demos-visits"
            badge={
              <span
                data-testid="due-appts-count-mobile"
                className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-sky-600 px-2 text-xs font-semibold text-white"
              >
                {appts.length}
              </span>
            }
          >
            Demos &amp; visits
          </SectionTitle>
          <p className="-mt-2 mb-2 text-xs text-zinc-500">Today + next 7 days — plan your travel ahead.</p>
          <div className="space-y-2">
            {appts.map((e) => (
              <EnquiryRow key={e.id} enquiry={e} onClick={() => setSelectedId(e.id)} showOverdue />
            ))}
          </div>
        </section>
      )}

      {/* Calls to make — today + overdue. Renders ONLY when it has calls. */}
      {callsDue.length > 0 && (
        <section className="mb-6" data-testid="mobile-calls-section">
          <SectionTitle
            testId="section-calls-due"
            badge={
              <span
                data-testid="due-calls-count-mobile"
                className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-rose-600 px-2 text-xs font-semibold text-white"
              >
                {callsDue.length}
              </span>
            }
          >
            Calls to make
          </SectionTitle>
          <div className="space-y-2">
            {callsDue.map((e) => (
              <EnquiryRow key={e.id} enquiry={e} onClick={() => setSelectedId(e.id)} showOverdue />
            ))}
          </div>
        </section>
      )}

      {/* Both empty — one calm reassurance card, never two empty halves. */}
      {appts.length === 0 && callsDue.length === 0 && (
        <section className="mb-6" data-testid="mobile-all-caught-up">
          <SectionTitle testId="section-due-today">What&apos;s due today</SectionTitle>
          <p className="rounded-xl border border-zinc-200 bg-white p-4 text-sm text-zinc-500">
            All caught up — nothing waiting on you.
          </p>
        </section>
      )}

      {/* New today */}
      <section className="mb-6">
        <SectionTitle testId="section-new-today">New today</SectionTitle>
        {newToday.length === 0 ? (
          <p className="rounded-xl border border-zinc-200 bg-white p-4 text-sm text-zinc-500">
            Nothing fresh yet today.
          </p>
        ) : (
          <div className="space-y-2">
            {newToday.map((e) => (
              <EnquiryRow key={e.id} enquiry={e} onClick={() => setSelectedId(e.id)} />
            ))}
          </div>
        )}
      </section>

      {/* All other open leads, compact */}
      <section className="mb-6">
        <SectionTitle testId="section-all-open">Everyone else</SectionTitle>
        <div className="space-y-2">
          {lineEnquiries
            .filter((e) => e.stage !== "signed_up" && e.stage !== "lost")
            .filter((e) => !dueIds.has(e.id) && !newToday.includes(e))
            .map((e) => (
              <EnquiryRow key={e.id} enquiry={e} onClick={() => setSelectedId(e.id)} />
            ))}
        </div>
      </section>

      {/* Sticky add button */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-zinc-200 bg-white p-3 sm:hidden">
        <div className="mx-auto max-w-md">
          <button
            data-testid="add-enquiry-button-mobile"
            data-tour-id="jigyasa-new-enquiry-btn"
            onClick={() => setAddOpen(true)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-4 text-base font-semibold text-white hover:bg-rose-700"
          >
            <Plus size={20} /> Add new enquiry
          </button>
        </div>
      </div>

      <EnquiryDetailSheet enquiry={selected} onClose={() => setSelectedId(null)} />
      <AddEnquirySheet open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
};
